import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { ArrowLeft, Boxes, Building2, ChevronRight, CreditCard, Gauge, KeyRound, LayoutDashboard, LayoutGrid, Link2, Plus, Shield, Users, Webhook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { SidebarFrame } from '@/components/sidebar-frame';
import { SidebarNav, type SidebarNavItem } from '@/components/sidebar-nav';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { OrgSwitcher } from '@/components/org-switcher';
import { OrgUsageBanner } from '@/components/org-usage-banner';
import { UsageProgress } from '@/components/usage-progress';
import { useCurrentRole } from '@/hooks/use-current-role';
import { useProfile } from '@/contexts/profile-context';
import { useSuperAdminAccess } from '@/hooks/use-super-admin-access';
import { useSidebarOrgUsageQuery } from '@/components/billing/queries';
import { useWorkspace } from '@/contexts/workspace-context';
import { orgUrl } from '@/lib/org-url';
import { workspaceUrl } from '@/lib/workspace-url';
import { isValidPublicId } from '@/lib/public-id';

// Full-width sub-item styling: drop the inset rail (mx/border-l/px) and the
// button's rounding so the active highlight spans the whole sidebar width like
// the parent items, with the content indented (pl-9) to keep the hierarchy read.
const SUB_LIST_FULL = 'mx-0 translate-x-0 border-l-0 px-0';
const SUB_BUTTON_FULL = 'h-8 w-full translate-x-0 rounded-none px-4 pl-9';

interface OrgSidebarProps {
  orgName: string | null;
  /** Opens the org-scoped Connect Channel modal (target picked in the modal). */
  onConnectClick: () => void;
}

/**
 * R3 Organization-view sidebar (spec Decision 2):
 *
 *   Workspaces        <- landing (org dashboard)
 *   Organization      <- all org members (rename is admin-gated on the page)
 *   Team              <- org-admin ONLY (Decision 4: non-admins never see it)
 *   Billing           <- org-admin ONLY (Decision 4: org member has no Billing)
 *
 * Active nav entries get a full-width highlight (no rounding/margin), matching
 * the workspace sidebar's channel list (operator revision 2026-06-13).
 */
export function OrgSidebar({ orgName, onConnectClick }: OrgSidebarProps) {
  const { setOpenMobile } = useSidebar();
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();
  // See AppSidebar: collapse is driven manually on `collapsible="none"` so the
  // base-ui Combobox (org switcher) keeps working — the fixed-container collapse
  // modes break its real-pointer toggle.
  const { role } = useCurrentRole();
  const isOrgAdmin = role === 'admin';
  const { profile } = useProfile();
  const { hasAccess: isSuperAdmin } = useSuperAdminAccess(profile?.email ?? null);
  const validOrgId = orgId && isValidPublicId(orgId, 'org') ? orgId : null;
  // Org-pooled usage for the sidebar meter (total messages this quota period across
  // every workspace in the org vs the org's plan quota). Billing is per-org,
  // so this is the org-correct counterpart to the workspace usage bar.
  // Usage is non-critical chrome — the query's transport suppresses the global
  // error page, so a failure never blocks the sidebar.
  // `undefined` = still loading (render a skeleton in the slot so the meter
  // never pops in and shoves the nav down); `null` = load failed (render
  // nothing); object = loaded.
  const usageQuery = useSidebarOrgUsageQuery(validOrgId);
  const usage = usageQuery.isError ? null : usageQuery.data;
  // Active-route matching for the full-width highlight. Workspaces is the org
  // index (exact); Team has a /:memberId detail route so it matches by prefix.
  const isActive = (path: string, exact = false) =>
    !!validOrgId &&
    (exact
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(`${path}/`));
  // The workspace the user came from: activeWorkspace persists from
  // localStorage while on org routes (no :ws param resets it). Falls back to
  // `/` (RootRedirect picks a workspace) for org admins with no workspaces.
  const { activeWorkspace, organizations } = useWorkspace();
  const backTo = activeWorkspace ? workspaceUrl(activeWorkspace.id) : '/';
  // AIT-241: the switcher now hosts "Create organization", so it renders for
  // single-org users too. The static org-name block remains only for the
  // organizations.length === 0 edge (org list still loading / none).
  const showOrgSwitcher = organizations.length >= 1;

  // SaaS section (D13): management features are available to every org.
  // Rendered as a collapsible parent ("SaaS") with the shadcn SidebarMenuSub
  // rail — Customers/Onboarding/Channels nest under it.
  const onboardingActive = validOrgId ? isActive(orgUrl(validOrgId, 'onboarding')) : false;
  const channelsActive = validOrgId ? isActive(orgUrl(validOrgId, 'channels')) : false;
  const customersActive = validOrgId ? isActive(orgUrl(validOrgId, 'customers')) : false;
  // Tri-state open: `null` = follow the route (auto-expand whenever a child
  // route is active, including client-side navigation into one); an explicit
  // boolean = the user toggled and their choice wins. Derived, so no effect is
  // needed to sync open-state with the route.
  const [saasToggled, setSaasToggled] = useState<boolean | null>(null);
  const saasOpen = saasToggled ?? (onboardingActive || channelsActive || customersActive);

  // Workspaces is a clickable-parent: the label links to the list, the chevron
  // expands its Channels sub-page (kind='team'). List match is EXACT so the
  // parent doesn't read active while on the /workspaces/channels child.
  const workspacesListActive = validOrgId ? isActive(orgUrl(validOrgId, 'workspaces'), true) : false;
  const workspacesChannelsActive = validOrgId
    ? isActive(orgUrl(validOrgId, 'workspaces/channels'))
    : false;
  const [workspacesToggled, setWorkspacesToggled] = useState<boolean | null>(null);
  const workspacesOpen = workspacesToggled ?? (workspacesListActive || workspacesChannelsActive);

  // Workspaces is rendered separately below as a clickable-parent, so topItems
  // is just Dashboard.
  const topItems: SidebarNavItem[] = [
    {
      icon: <LayoutDashboard className="size-4" />,
      label: 'Dashboard',
      to: validOrgId ? orgUrl(validOrgId) : '#',
      isActive: validOrgId ? isActive(orgUrl(validOrgId), true) : false,
    },
  ];

  const adminItems: SidebarNavItem[] = [
    {
      icon: <Building2 className="size-4" />,
      label: 'Organization',
      to: validOrgId ? orgUrl(validOrgId, 'organization') : '#',
      isActive: validOrgId ? isActive(orgUrl(validOrgId, 'organization')) : false,
    },
    ...(isOrgAdmin
      ? ([
          {
            icon: <KeyRound className="size-4" />,
            label: 'API keys',
            to: validOrgId ? orgUrl(validOrgId, 'api') : '#',
            isActive: validOrgId ? isActive(orgUrl(validOrgId, 'api')) : false,
          },
          {
            icon: <Users className="size-4" />,
            label: 'Team',
            to: validOrgId ? orgUrl(validOrgId, 'team') : '#',
            isActive: validOrgId ? isActive(orgUrl(validOrgId, 'team')) : false,
          },
          {
            icon: <Gauge className="size-4" />,
            label: 'Plans',
            to: validOrgId ? orgUrl(validOrgId, 'plans') : '#',
            isActive: validOrgId ? isActive(orgUrl(validOrgId, 'plans')) : false,
          },
          {
            icon: <CreditCard className="size-4" />,
            label: 'Billing',
            to: validOrgId ? orgUrl(validOrgId, 'billing') : '#',
            isActive: validOrgId ? isActive(orgUrl(validOrgId, 'billing')) : false,
          },
        ] satisfies SidebarNavItem[])
      : []),
    // Same placement as the workspace sidebar: last nav entry, super-admins only.
    ...(isSuperAdmin
      ? [{ icon: <Shield className="size-4" />, label: 'Super Admin', to: '/super-admin/organizations' }]
      : []),
  ];

  return (
    <SidebarFrame>
      {/* Fixed h-12 + border-b so the switcher's bottom edge lines up with the
          main content header (also h-12 border-b) — one continuous top line. */}
      <SidebarHeader className="h-12 !p-0 border-b">
        {showOrgSwitcher ? (
          <OrgSwitcher />
        ) : (
          <div className="flex h-12 w-full items-center gap-2 px-4">
            <div className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-semibold shrink-0">
              {orgName ? orgName.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
              {orgName ? (
                <>
                  <span className="truncate font-semibold">{orgName}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    Organization
                  </span>
                </>
              ) : (
                <Skeleton className="h-4 w-24" />
              )}
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        {/* Connect Channel: org-scoped — the modal picks a workspace/customer
            target. Same placement as the workspace sidebar (top of content). */}
        <div className="px-4 pt-6 pb-6 group-data-[collapsible=icon]:px-1.5">
          <Button
            variant="default"
            size="default"
            className="w-full gap-2 group-data-[collapsible=icon]:px-0"
            onClick={() => {
              setOpenMobile(false); // close the mobile drawer before the modal opens
              onConnectClick();
            }}
            title="Connect Channel"
          >
            <Plus className="size-4 hidden group-data-[collapsible=icon]:block" />
            <span className="group-data-[collapsible=icon]:hidden">Connect Channel</span>
          </Button>
        </div>
        {/* Reserve the meter's slot with a same-height skeleton while loading so
            it never appears out of nowhere and pushes the nav down. */}
        {usage === undefined ? (
          <>
            <div className="border-t border-b px-4 py-3 group-data-[collapsible=icon]:hidden">
              {/* h-5 matches UsageProgress's text-sm line so the slot height is
                  identical before and after load — no shift on swap. */}
              <div className="flex h-5 items-center gap-3">
                <Skeleton className="h-3.5 w-12" />
                <Skeleton className="h-1.5 flex-1 rounded-full" />
                <Skeleton className="h-3.5 w-16" />
              </div>
            </div>
            {/* Collapsed icon-rail slot — bar-only skeleton. h-5 matches the
                expanded row's text-sm line so the slot height is identical and
                the nav below never shifts on collapse/expand. */}
            <div className="hidden border-t border-b px-2.5 py-3 group-data-[collapsible=icon]:block">
              <div className="flex h-5 items-center">
                <Skeleton className="h-1.5 w-full rounded-full" />
              </div>
            </div>
          </>
        ) : usage ? (
          <>
            <div className="border-t border-b px-4 py-3 group-data-[collapsible=icon]:hidden">
              <UsageProgress
                current={usage.total}
                limit={usage.limit}
                label="Usage"
                labelInfo={
                  usage.usageUnit === 'actions'
                    ? 'Total actions across this organization. Includes sending a message, replying to a comment, hiding or unhiding a comment, and publishing a post, reel, or story.'
                    : "Total messages across every workspace and customer in this organization this quota period, counted against your plan's quota. Includes messages sent directly from the WhatsApp, Instagram or Facebook apps."
                }
                unitLabel={usage.usageUnit === 'actions' ? 'actions' : 'messages'}
                abbreviate
              />
            </div>
            {/* Collapsed icon-rail: keep the usage bar visible (count on hover).
                h-5 matches the expanded row's text-sm line so the slot height is
                identical and the nav below never shifts on collapse/expand. */}
            <div className="hidden border-t border-b px-2.5 py-3 group-data-[collapsible=icon]:block">
              <div className="flex h-5 items-center">
                <UsageProgress
                  current={usage.total}
                  limit={usage.limit}
                  label="Usage"
                  variant="rail"
                  unitLabel={usage.usageUnit === 'actions' ? 'actions' : 'messages'}
                />
              </div>
            </div>
          </>
        ) : null}
        {/* No horizontal padding on the menu + rounded-none px-4 on each button
            so the active highlight spans the full sidebar width, matching the
            workspace sidebar's channel list. */}
        <SidebarMenu>
          <SidebarNav items={topItems} />

            {/* Workspaces: clickable-parent. The label links to the workspaces
                list; the chevron (admins only) expands its Channels sub-page.
                Non-admins see a plain link, exactly as before. */}
            <SidebarMenuItem>
              <SidebarMenuButton
                render={<Link to={validOrgId ? orgUrl(validOrgId, 'workspaces') : '#'} />}
                isActive={workspacesListActive}
                className="rounded-none px-4"
                tooltip="Workspaces"
              >
                <LayoutGrid className="size-4" />
                <span className="group-data-[collapsible=icon]:hidden">Workspaces</span>
              </SidebarMenuButton>
              {isOrgAdmin && (
                <>
                  <SidebarMenuAction
                    onClick={() => setWorkspacesToggled(!workspacesOpen)}
                    aria-label="Toggle Workspaces section"
                    aria-expanded={workspacesOpen}
                    // right-3.5 lines the chevron tip up with the SaaS chevron,
                    // which sits inline at the button's px-4 (16px) right edge.
                    className="right-3.5 group-data-[collapsible=icon]:hidden"
                  >
                    <ChevronRight
                      className={cn('size-4 transition-transform', workspacesOpen && 'rotate-90')}
                    />
                  </SidebarMenuAction>
                  {workspacesOpen && (
                    <SidebarMenuSub className={SUB_LIST_FULL}>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          className={SUB_BUTTON_FULL}
                          render={
                            <Link to={validOrgId ? orgUrl(validOrgId, 'workspaces/channels') : '#'} />
                          }
                          isActive={workspacesChannelsActive}
                        >
                          <Webhook className="size-4" />
                          <span>Channels</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  )}
                </>
              )}
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => setSaasToggled(!saasOpen)}
                // When collapsed, surface that a child route is active so the
                // parent still reads as "current"; when open, the active child
                // carries the highlight instead.
                isActive={!saasOpen && (onboardingActive || channelsActive || customersActive)}
                aria-expanded={saasOpen}
                className="rounded-none px-4"
                tooltip="SaaS"
              >
                <Boxes className="size-4" />
                {/* SaaS label + chevron carry the menu's only non-last-child
                    span, so the cva's [&>span:last-child]:hidden can't reach
                    them — hide both explicitly when the rail collapses. */}
                <span className="group-data-[collapsible=icon]:hidden">SaaS</span>
                <ChevronRight
                  className={cn(
                    'ml-auto size-4 transition-transform group-data-[collapsible=icon]:hidden',
                    saasOpen && 'rotate-90',
                  )}
                />
              </SidebarMenuButton>
              {saasOpen && (
                <SidebarMenuSub className={SUB_LIST_FULL}>
                  {isOrgAdmin && (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        className={SUB_BUTTON_FULL}
                        render={<Link to={validOrgId ? orgUrl(validOrgId, 'customers') : '#'} />}
                        isActive={customersActive}
                      >
                        <Users className="size-4" />
                        <span>Customers</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )}
                  {isOrgAdmin && (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        className={SUB_BUTTON_FULL}
                        render={<Link to={validOrgId ? orgUrl(validOrgId, 'onboarding') : '#'} />}
                        isActive={onboardingActive}
                      >
                        <Link2 className="size-4" />
                        <span>Onboarding</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )}
                  {isOrgAdmin && (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        className={SUB_BUTTON_FULL}
                        render={<Link to={validOrgId ? orgUrl(validOrgId, 'channels') : '#'} />}
                        isActive={channelsActive}
                      >
                        <Webhook className="size-4" />
                        <span>Customer Channels</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )}
                </SidebarMenuSub>
              )}
            </SidebarMenuItem>

          <SidebarNav items={adminItems} />
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter>
        {isOrgAdmin && validOrgId && usage ? (
          <div className="px-1 pb-1">
            <OrgUsageBanner
              orgId={validOrgId}
              percentage={usage.percentage}
              unitLabel={usage.usageUnit === 'actions' ? 'actions' : 'messages'}
              channelsPaused={usage.channelsPaused}
              reserveBalance={usage.reserveBalance}
            />
          </div>
        ) : null}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton render={<Link to={backTo} />} tooltip="Go Back">
              <ArrowLeft className="size-4" />
              <span>Go Back</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </SidebarFrame>
  );
}
